import { useCallback, useState } from 'react';
import { useSupabase } from './useSupabase';
import { Company, CreateCompanyDTO, UpdateCompanyDTO } from '@/types/company';
import { toast } from 'sonner';

export function useCompany() {
  const { supabase } = useSupabase();
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const fetchCompanies = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('company')
        .select('*')
        .order('name');

      if (error) throw error;

      return data as Company[];
    } catch (error) {
      console.error('Erro ao buscar empresas:', error);
      toast.error('Erro ao carregar empresas');
      return [];
    } finally {
      setIsLoading(false);
    }
  }, [supabase]);

  const createCompany = useCallback(
    async (data: CreateCompanyDTO) => {
      try {
        const { data: company, error } = await supabase
          .from('company')
          .insert([data])
          .select()
          .single();

        if (error) throw error;

        // Criar relação com o usuário
        const { error: relationError } = await supabase
          .from('user_company')
          .insert([
            {
              company_id: company.id,
              user_id: (await supabase.auth.getUser()).data.user?.id,
              role: 'owner',
            },
          ]);

        if (relationError) throw relationError;

        toast.success('Empresa criada com sucesso');
        return company as Company;
      } catch (error) {
        console.error('Erro ao criar empresa:', error);
        toast.error('Erro ao criar empresa');
        return null;
      }
    },
    [supabase]
  );

  const updateCompany = useCallback(
    async (id: string, data: UpdateCompanyDTO) => {
      try {
        const { data: company, error } = await supabase
          .from('company')
          .update({
            ...data,
            updated_at: new Date(),
          })
          .eq('id', id)
          .select()
          .single();

        if (error) throw error;

        toast.success('Empresa atualizada com sucesso');
        return company as Company;
      } catch (error) {
        console.error('Erro ao atualizar empresa:', error);
        toast.error('Erro ao atualizar empresa');
        return null;
      }
    },
    [supabase]
  );

  const setCompany = useCallback((company: Company | null) => {
    setCurrentCompany(company);
    if (company) {
      localStorage.setItem('currentCompanyId', company.id);
    } else {
      localStorage.removeItem('currentCompanyId');
    }
  }, []);

  return {
    currentCompany,
    isLoading,
    fetchCompanies,
    createCompany,
    updateCompany,
    setCompany,
  };
} 